import React, { useContext } from 'react';
import { Form, Card, Button } from 'react-bootstrap';

import cx from 'classnames';
import {
  CUSTOM_AMOUNT_MESSAGE,
  DonateNowFormSteps,
  IDonateNowForm,
} from 'utils/checkInterface';

import { AuthContext } from 'contexts/authContext';
import { IDonationFund } from 'models/donationFund';
import { Frequency } from 'models/frequency';

interface DonateNowSummaryProps {
  donationOption: any;
  onStepChange: (step: DonateNowFormSteps) => void;
  onPurchase(
    value: IDonateNowForm,
    asAGuest: boolean,
    createNewAccountTabActive: boolean,
  ): void;
  frequency: Frequency; // 'OnceOff' | 'Monthly'
  customAmount?: number | string;
  donationFund?: IDonationFund;
  donationDay?: number; // 15 | 28;
  canChangeFrequency?: boolean;
}

const DonateNowSummary: React.FC<DonateNowSummaryProps> = ({
  donationOption,
  onPurchase,
  frequency,
  onStepChange,
  customAmount,
  donationFund,
  donationDay,
  canChangeFrequency,
}) => {
  const stringToInt = (value: string) => parseInt(value, 10);
  const {
    state: { user },
  } = useContext(AuthContext);

  const purchaseNow = (asAGuest = false, createNewAccountTabActive = false) => {
    onPurchase(
      {
        donationOption,
        donationFund,
        frequency,
        customAmount: !customAmount ? undefined : customAmount,
        donationDay,
      },
      asAGuest,
      createNewAccountTabActive,
    );
  };

  return (
    <Card
      className={cx(
        'donate-now-card-mobile',
        'position-relative',
        'overflow-hidden',
      )}
    >
      <Card.Body>
        <Form onSubmit={ev => ev.preventDefault()}>
          <div className="pb-3 rounded-corners">
            <h4 className="text-center mb-3 text-bold">Order summary</h4>
            <Form.Group>
              <Card className="bg-primary text-white">
                <Card.Body>
                  <div className="mb-2">
                    <h4 className="text-center mb-3 text-bold">
                      Thank you for your support
                    </h4>
                    <div className="d-flex justify-content-between mb-1">
                      <h4 className={cx('text-bold', 'm-0')}>
                        $
                        {stringToInt(donationOption?.amount?.toString()) ||
                          stringToInt(customAmount?.toString() || '0')}
                      </h4>
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectDonation)
                        }
                      >
                        Change donation
                      </Button>
                    </div>
                    {donationFund && <p>{donationFund.heading}</p>}
                    {donationOption ? (
                      <p>{donationOption.title}</p>
                    ) : (
                      CUSTOM_AMOUNT_MESSAGE
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-end mb-2">
                    <div>
                      <h5>Frequency</h5>
                      <h4 className={cx('text-bold', 'm-0')}>
                        {frequency === Frequency.OnceOff
                          ? 'Single'
                          : Frequency.Monthly}
                      </h4>
                    </div>
                    {canChangeFrequency && (
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectFrequency)
                        }
                      >
                        Change frequency
                      </Button>
                    )}
                  </div>
                  {donationDay && (
                    <div className="d-flex justify-content-between">
                      <div>
                        <small>Day of Month</small>
                        <h4 className={cx('text-bold')}>
                          {donationDay}th of Month
                        </h4>
                      </div>
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectDonationDate)
                        }
                      >
                        Change date
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Form.Group>
            {(!user?.email && (
              <>
                <h4 className="text-center mt-3 mb-4">
                  How would you like to checkout?
                </h4>
                <Button
                  onClick={() => purchaseNow(false)}
                  variant="outline-primary"
                  className="d-block m-auto"
                >
                  Checkout with my surf account
                </Button>
                <Button
                  className="d-block dark-grey-color mt-2 mx-auto text-black"
                  variant="link"
                  onClick={() => purchaseNow(false, true)}
                >
                  Don't have an account? Create one today and checkout faster in
                  the future.
                </Button>
                {frequency === 'OnceOff' && (
                  <Button
                    data-cy="button-checkout-without-account"
                    onClick={() => purchaseNow(true)}
                    variant="outline-primary"
                    className="d-block m-auto"
                  >
                    Checkout without an account
                  </Button>
                )}
              </>
            )) || (
              <Button
                onClick={() => purchaseNow(false)}
                variant="outline-primary"
                className="d-block m-auto"
              >
                Checkout
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default DonateNowSummary;
